import { useRef, useState } from 'react';
import {
  Upload,
  X,
  Loader2,
  ExternalLink,
  Image as ImageIcon,
  FileText,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  attachmentService,
  type AttachmentEntityType,
} from '../../services/httpServices/attachmentService';

interface AttachmentUploadFieldProps {
  /** Field label rendered above the control. */
  label?: string;
  /** Currently stored public URL (empty string when none). */
  value: string;
  /** Called with the uploaded public URL, or `''` when removed. */
  onChange: (url: string) => void;
  /** Attachment bucket this file is stored under. */
  entityType?: AttachmentEntityType;
  /** Logical owner id (e.g. `expense-receipt`). */
  entityId: string;
  /** Optional description persisted with the attachment record. */
  description?: string;
  /** Accepted mime types / extensions for the file picker. */
  accept?: string;
  /** Client-side guard; the server also enforces max 50 MB. */
  maxSizeMB?: number;
  disabled?: boolean;
  /** Small hint shown under the field. */
  helperText?: string;
}

const DEFAULT_ACCEPT = 'image/*,application/pdf';
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;

/**
 * Upload control for a single receipt / document. Uploads the selected file to
 * the shared attachments endpoint and exposes only the returned public URL via
 * `onChange`, so forms never persist large payloads.
 */
export function AttachmentUploadField({
  label,
  value,
  onChange,
  entityType = 'general',
  entityId,
  description,
  accept = DEFAULT_ACCEPT,
  maxSizeMB = 50,
  disabled,
  helperText,
}: AttachmentUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isImage = IMAGE_EXT.test(value.split('?')[0]);
  const fileName = (() => {
    const path = value.split('?')[0];
    const name = decodeURIComponent(path.substring(path.lastIndexOf('/') + 1));
    return name || 'attachment';
  })();

  const pick = () => inputRef.current?.click();

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setError(null);

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File is larger than ${maxSizeMB} MB`);
      return;
    }

    setUploading(true);
    try {
      const res = await attachmentService.uploadFile(
        file,
        entityType,
        entityId,
        description ?? file.name,
      );
      const url = res.data?.publicUrl;
      if (!url) throw new Error('Upload did not return a file URL');
      onChange(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="space-y-1.5">
      {label && <label className="block text-sm font-medium text-text">{label}</label>}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled || uploading}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {value && !uploading ? (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-background p-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10">
            {isImage ? (
              <img src={value} alt="" className="h-full w-full object-cover" />
            ) : (
              <FileText size={20} className="text-primary" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-text">{fileName}</p>
            <p className="text-xs text-text-muted">Attachment ready</p>
          </div>
          <div className="flex items-center gap-1">
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg p-2 text-text-muted transition-colors hover:bg-primary/10 hover:text-primary"
              title="View"
            >
              <ExternalLink size={16} />
            </a>
            <button
              type="button"
              onClick={pick}
              disabled={disabled}
              className="rounded-lg p-2 text-text-muted transition-colors hover:bg-primary/10 hover:text-primary disabled:opacity-50"
              title="Replace"
            >
              <RefreshCw size={16} />
            </button>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setError(null);
              }}
              disabled={disabled}
              className="rounded-lg p-2 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
              title="Remove"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          disabled={disabled || uploading}
          className={cn(
            'flex w-full items-center gap-3 rounded-xl border-2 border-dashed px-4 py-5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
            dragOver
              ? 'border-primary bg-primary/5'
              : 'border-border bg-background hover:border-primary/50 hover:bg-primary/5',
          )}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            {uploading ? (
              <Loader2 size={20} className="animate-spin text-primary" />
            ) : (
              <Upload size={20} className="text-primary" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-text">
              {uploading ? 'Uploading…' : 'Click to upload or drag & drop'}
            </p>
            <p className="text-xs text-text-muted">
              Receipts, invoices or scans · images &amp; PDF, up to {maxSizeMB} MB
            </p>
          </div>
          <span className="hidden shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text sm:inline-flex">
            <ImageIcon size={14} />
            Browse
          </span>
        </button>
      )}

      {error && (
        <p className="flex items-center gap-1.5 text-xs text-danger">
          <AlertCircle size={13} />
          {error}
        </p>
      )}
      {!error && helperText && <p className="text-xs text-text-muted">{helperText}</p>}
    </div>
  );
}
